import { minuteMs } from "verdi-common/timeConstants";
import { useGetMyOrganizationSettings } from "@/api/orval/generated/organization";
import type { Zone } from "@/api/orval/generated/schemas";
import { useListZones } from "@/api/orval/generated/zone";
import { POSTHOG_FEATURE_FLAG, usePostHogFeatureFlag } from "@/services/feature-flags/posthog-feature-flags";

/**
 * On-demand irrigation is deliberately offered only on zones using a low-latency communication option ("classB communication"),
 * not a technical requirement, but a business decision to encourage adoption of the faster-response-time option.
 *
 * Grandfathered orgs are exempt via the response-time bypass flag, and get the feature unconditionally.
 */
const RESPONSIVE_ACTUATION_WINDOW_MS = minuteMs;

function doesZoneActuateQuickly(zone: Zone): boolean {
    const actuationDelayMs = zone.responseTimeStats?.theoreticalP95ActuationDelayMs;

    // Stats only exist for zones with a valve we can drive. Consider zones without stats (or without any devices) to be slow.
    return actuationDelayMs !== undefined && actuationDelayMs < RESPONSIVE_ACTUATION_WINDOW_MS;
}

/** The two PostHog flags this feature reads: whether it is released, and whether this org is a special case. */
function useOnDemandIrrigationFlags() {
    const isReleasedByPostHog = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAG.ON_DEMAND_IRRIGATION);
    const isBypassedByPostHog = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAG.ON_DEMAND_IRRIGATION_RESPONSE_TIME_BYPASS);

    return {
        isReleased: isReleasedByPostHog,
        isResponseTimeBypassed: isBypassedByPostHog,
    };
}

/**
 * The org's live zones. Deleted zones are filtered here rather than trusted to the endpoint because
 * this cache is also seeded from the session payload, which fetches zones by its own query.
 */
function useLiveZones(): Zone[] {
    const { data: zones } = useListZones();

    return zones?.filter((zone) => !zone.deleted) ?? [];
}

/**
 * Whether the org has access to the on-demand irrigation feature
 *
 * NOTE: separate from the org setting that is a separate control that the org can use to turn the feature off for
 * themselves if it is already enabled for that org.
 */
export function useIsOnDemandIrrigationAvailableForOrganization(): boolean {
    const { isReleased, isResponseTimeBypassed } = useOnDemandIrrigationFlags();
    const zones = useLiveZones();

    return isReleased && (isResponseTimeBypassed || zones.some(doesZoneActuateQuickly));
}

/** Whether a given zone should offer on-demand irrigation. Combines the org's setting and the zone's response time. */
export function useIsOnDemandIrrigationAvailableForZone(zoneId: string): boolean {
    const { isReleased, isResponseTimeBypassed } = useOnDemandIrrigationFlags();
    const { data: settings } = useGetMyOrganizationSettings();
    const zones = useLiveZones();

    // Zones are addressed by _id here and by id on the legacy model the card passes in.
    const zone = zones.find((candidate) => candidate._id === zoneId);

    // Hidden until the setting and zone arrive; showing the feature then withdrawing it shifts the card.
    if (!isReleased || !settings?.onDemandIrrigationEnabled || !zone) {
        return false;
    }

    return isResponseTimeBypassed || doesZoneActuateQuickly(zone);
}
